import React, { useContext, useEffect, useState } from 'react';
import * as sessionPreferences from '../state/preferences';
import { tokenState } from '../state/apolloVars';

export type SessionContextValue = {
  session: sessionPreferences.Session;
  setSession: (session: Partial<sessionPreferences.Session>) => void;
  clearSession: () => void;
};

export const SessionContext = React.createContext({} as SessionContextValue);

export type SessionProviderProps = {
  children: React.ReactNode;
};

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [session, setSessionInState] = useState<sessionPreferences.Session>({
    authorized: false,
  });

  const clearSession = () => {
    setSessionInState({ authorized: false });
    sessionPreferences.setSession({ authorized: false });
    tokenState({});
  };

  const setSession = (session: Partial<sessionPreferences.Session>) => {
    setSessionInState(oldSession => {
      const newSession = {
        ...oldSession,
        ...session,
      };
      sessionPreferences.setSession(newSession);
      // Update tokenState if session has token information
      if (
        newSession.token &&
        newSession.expiredDate &&
        newSession.refreshToken
      ) {
        tokenState({
          token: newSession.token,
          expiredDate: newSession.expiredDate,
          refreshToken: newSession.refreshToken,
        });
      }
      return newSession;
    });
  };

  const getSession = async () => {
    return (await sessionPreferences.getSession()) as sessionPreferences.Session;
  };

  useEffect(() => {
    (async () => {
      const session = await getSession();
      setSessionInState(session);
      // Set tokenState if session has token information
      if (session.token && session.expiredDate && session.refreshToken) {
        tokenState({
          token: session.token,
          expiredDate: session.expiredDate,
          refreshToken: session.refreshToken,
        });
      }
    })();
  }, []);

  const value = {
    session,
    setSession,
    clearSession,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): SessionContextValue =>
  useContext<SessionContextValue>(SessionContext);
